import {useEffect} from 'react';
import {useSelector} from 'react-redux';
import * as Yup from 'yup';
import {useTranslation} from 'react-i18next';
import {getCategories} from '../../../../../redux/categories/categoriesActions';
import {getPlan, createPlan, updatePlan, linkPriceGroupsToPlan, unlinkPriceGroupsFromPlan} from '../../../../../redux/subscription/subscriptionActions';
import setGlobalState from '../../../../../redux/setGlobalState';
import store from '../../../../../store';
import {combineDateTime} from '../../../../../library/dates';
import {DEFAULT_TIME_ZONE} from '../../../../../globalVariables/locals';
import {createDateAfterValidator} from '../../../../../functions/validationsYup';
import {SubscriptionFormValues} from '../createSubscriptionForm.types';
import { TState } from '../../../../../redux/store.type';

interface UseSubscriptionFormProps {
    planTid?: number | null;
}

export const useSubscriptionForm = ({planTid}: UseSubscriptionFormProps) => {
    const {t} = useTranslation('subscriptions');
    const {t: tErrors} = useTranslation('errors');
    const categoriesData = useSelector((state: TState) => state.categories.eventCategories);
    const planData = useSelector((state: TState) =>
        state.subscription.subscriptionPlans?.find(p =>
            p.planTid === planTid
        )
    );

    useEffect(() => {
        const loadData = async () => {
            if(!categoriesData?.length) {
                await getCategories();
            }
            if(planTid) {
                await getPlan(planTid);
            } else {
                setGlobalState('subscription.currentPlan', null);
            }
        };
        loadData();
    }, [planTid]);

    const validationSchema = Yup.object({
        subscriptionType: Yup.string().required(t('subscriptionTypeRequired')),
        subscriptionName: Yup.string().required(t('subscriptionNameRequired')),
        category: Yup.array().min(1, t('categoryRequired')),
        saleStartDate: Yup.number().when('activateSaleValidity', {
            is: (activateSaleValidity) => activateSaleValidity === true,
            then: (schema) => schema.required(tErrors('requiredError', {name: t('saleStartDate')}))
        }),
        saleStartTime: Yup.number().when('activateSaleValidity', {
            is: (activateSaleValidity) => activateSaleValidity === true,
            then: Yup.number().required(tErrors('requiredError', {name: t('saleStartTime')}))
        }),
        saleEndDate: Yup.number().when('activateSaleValidity', {
            is: (activateSaleValidity) => activateSaleValidity === true,
            then: (schema) => schema
                .required(tErrors('requiredError', {name: t('saleEndDate')}))
                .concat(createDateAfterValidator(
                    'saleEndDate',
                    t('saleEndDateBeforeStartDateError'),
                    'saleStartDate',
                    'saleStartTime',
                    null,
                    'saleEndTime'
                ))
        }),
        saleEndTime: Yup.number().when('activateSaleValidity', {
            is: (activateSaleValidity) => activateSaleValidity === true,
            then: Yup.number().required(tErrors('requiredError', {name: t('saleEndTime')}))
        }),
        redemptionEndDate: Yup.number().when(['activateSaleValidity', 'activateRedemptionValidity', 'saleEndDate', 'saleEndTime'], {
            is: (activateSaleValidity, activateRedemptionValidity, saleEndDate, saleEndTime) => 
                activateSaleValidity && activateRedemptionValidity && saleEndDate && saleEndTime,
            then: createDateAfterValidator(
                'redemptionEndDateAfterSaleEndDate',
                t('redemptionEndDateAfterSaleEndDateError'),
                'saleEndDate',
                'saleEndTime',
                null,
                'redemptionEndTime'
            )
        }),
        redemptionEndTime: Yup.number(),
    });

    const getInitialValues = (): SubscriptionFormValues => {
        // Like in newEvent/globalSteps.js - use the same timestamp for both date and time
        // DateInput and TimeInput with isTimestamp=true will parse it internally
        return {
            subscriptionType: planData?.planType || 'open',
            subscriptionName: planData?.title || '',
            category: planData?.categories?.map(c => c.catId) || [],
            active: planData?.active !== undefined ? planData.active : true,
            image: planData?.image || null,
            description: planData?.description || '',
            // Pricing fields
            prices: planData?.prices || [],
            selectedPrices: planData?.selectedPrices || [],
            // Redemption Settings
            redemptionPrice: planData?.redemptionPrice || null,
            redemptionUpToPrice: planData?.redemptionUpToPrice || null,
            priceGroupIds: planData?.priceGroups?.map(pg => pg.spgId) || [],
            // Sale Validity - same timestamp for both date and time, like in newEvent
            saleStartDate: planData?.saleStart || null,
            saleStartTime: planData?.saleStart || null,
            saleEndDate: planData?.saleEnd || null,
            saleEndTime: planData?.saleEnd || null,
            activateSaleValidity: planData?.saleStart || planData?.saleEnd ? true : false,
            // Redemption Validity
            redemptionEndDate: planData?.usageDeadline || null,
            redemptionEndTime: planData?.usageDeadline || null,
            activateRedemptionValidity: planData?.usageDeadline ? true : false,
            // Ticketing Settings
            // fixedBarcode: planData?.fixedBarcode || false,
            // singleTicket: planData?.singleTicket || false,
            // Series Group Assignment
            // seriesGroups: planData?.seriesGroups || []
        };
    };

    const handleSubmit = async (values: SubscriptionFormValues) => {
        const planData = {
            title: values.subscriptionName,
            planType: values.subscriptionType,
            active: values.active ? 1 : 0,
            categoryIds: values.category || [],
            image: values.image,
            description: values.description,
            // Pricing fields
            prices: values.prices || [],
            selectedPrices: values.selectedPrices || [],
            // Sale Validity
            saleStart: values.activateSaleValidity ? combineDateTime(values.saleStartDate, values.saleStartTime, DEFAULT_TIME_ZONE) : null,
            saleEnd: values.activateSaleValidity ? combineDateTime(values.saleEndDate, values.saleEndTime, DEFAULT_TIME_ZONE) : null,
            usageDeadline: values.activateRedemptionValidity ? combineDateTime(values.redemptionEndDate, values.redemptionEndTime, DEFAULT_TIME_ZONE) : null,
        };

        let createdPlanTid = planTid;

        if(planTid) {
            await updatePlan(planTid, planData);
        } else {
            await createPlan(planData);
            // Get the created plan TID from Redux state
            const currentPlan = store.getState().subscription.currentPlan;
            createdPlanTid = currentPlan;
        }

        // Handle price groups linking/unlinking
        if(createdPlanTid && values.priceGroupIds) {
            // Get current linked price groups
            const currentPlan = await getPlan(createdPlanTid);
            const currentPriceGroupIds = currentPlan?.plan?.priceGroups?.map(pg => pg.spgId) || [];
            const newPriceGroupIds = values.priceGroupIds || [];

            // Find groups to link (in new but not in current)
            const toLink = newPriceGroupIds.filter((id: number) => !currentPriceGroupIds.includes(id));
            // Find groups to unlink (in current but not in new)
            const toUnlink = currentPriceGroupIds.filter((id: number) => !newPriceGroupIds.includes(id));

            // Link new groups and unlink removed groups in parallel (batch operations)
            const promises = [];
            if (toLink.length > 0) {
                promises.push(linkPriceGroupsToPlan(createdPlanTid, toLink));
            }
            if (toUnlink.length > 0) {
                promises.push(unlinkPriceGroupsFromPlan(createdPlanTid, toUnlink));
            }
            if (promises.length > 0) {
                await Promise.all(promises);
            }
        }
    };

    return {
        validationSchema,
        getInitialValues,
        handleSubmit
    };
};

